import { useCallback, useEffect, useMemo, useState } from "react";
import { UserCog, ChevronRight, Search, Users, Network, Phone, Info, Printer, Lock, MapPin, Palmtree, CheckCircle2, UserCheck, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DIVISION_COLOR } from "@/lib/constants";
import ProfileDialog from "@/components/ProfileDialog";
import OrgTree from "@/components/OrgTree";
import OrgChartVisual from "@/components/OrgChartVisual";
import OnLeaveWidget from "@/components/OnLeaveWidget";
import UserFormDialog from "@/components/user-management/UserFormDialog";

// 149-cw-r149: preset form utk Add User dari halaman Team & Divisions.
// Struktur harus match yg dipakai UserManagementPage.jsx supaya UserFormDialog
// bisa reuse tanpa modifikasi.
const EMPTY_ADD_USER_FORM = { name: "", email: "", password: "", division: "", sub_division: "", level: 3, position: "", preset_id: "" };

const DIV_ORDER = ["BOD", "FAT", "HRGA", "Procurement Asset Logistic", "Project"];

export default function TeamPage() {
  const { user } = useAuth();
  const { canAccess, canPerm } = useAccess();
  const canPrintPayroll = canAccess(user, "payroll_print");
  const [payrollDlg, setPayrollDlg] = useState(false);
  const [payrollPwd, setPayrollPwd] = useState("");
  const [payrollBusy, setPayrollBusy] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [query, setQuery] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [tab, setTab] = useState("list"); // "list" | "org" | "by-city"
  // Route via matrix `personnel_manage.update` — sebelumnya hardcoded BOD/HRGA. Admin
  // bisa delegate ke divisi lain via Access Control. BOD Lv1 super-admin bypass otomatis.
  const canManage = canPerm(user, "personnel_manage", "update");
  // Level visibility: user dengan view perm dapat lihat detail semua level. Sebelumnya
  // hardcoded BOD any lv / FAT Lv2 / HRGA Lv2 → sekarang matrix view perm.
  const canSeeAllLevels = canPerm(user, "personnel_manage", "view");
  // 149-cw-r149: gate Add User pakai perm yang SAMA dgn User Management supaya
  // konsisten — `user_management.create`. Backend `/api/admin/users` POST validate
  // ulang → aman meski frontend bocor.
  const canCreateUser = canPerm(user, "user_management", "create");

  // 149-cw-r149: state Add User dialog + reference lists (divisions/sub/positions).
  const [addUserDialog, setAddUserDialog] = useState({ open: false, mode: "create", data: EMPTY_ADD_USER_FORM });
  const [addUserBusy, setAddUserBusy] = useState(false);
  const [divisions, setDivisions] = useState([]);
  const [subDivisions, setSubDivisions] = useState([]);
  const [positions, setPositions] = useState([]);

  const load = useCallback(() => api.get("/users/team").then(({ data }) => setUsers(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  // 149-cw-r149: lazy-load refs HANYA kalau user memiliki akses create — hindari
  // request tidak perlu utk viewer biasa.
  useEffect(() => {
    if (!canCreateUser) return;
    let cancelled = false;
    Promise.all([api.get("/divisions"), api.get("/sub-divisions"), api.get("/positions")])
      .then(([divs, subs, poss]) => {
        if (cancelled) return;
        setDivisions(divs.data || []);
        setSubDivisions(subs.data || []);
        setPositions(poss.data || []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [canCreateUser]);

  const openAddUser = () => setAddUserDialog({ open: true, mode: "create", data: { ...EMPTY_ADD_USER_FORM } });
  const submitAddUser = async (e) => {
    e.preventDefault();
    setAddUserBusy(true);
    try {
      await api.post("/admin/users", addUserDialog.data);
      toast.success("User berhasil ditambahkan");
      setAddUserDialog((s) => ({ ...s, open: false }));
      await load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally { setAddUserBusy(false); }
  };

  // Refresh users after profile edit to pick up reports_to/role_title changes
  const onDialogClose = () => { setSelectedId(null); load(); };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="overline text-slate-500">Structure</p>
          <h1 className="font-display text-3xl tracking-tight text-[#0B1D3A]">Team & Divisions</h1>
          <p className="text-sm text-slate-500 mt-1">
            Semua personel di organisasi. Klik tab <span className="font-semibold">Struktur Organisasi</span> untuk melihat bagan hierarki.
            {canManage ? (
              <span className="ml-2 inline-flex items-center gap-1 text-[11px] text-[#FF5A1F] font-semibold uppercase tracking-wider">
                <UserCog size={12} /> Klik personil untuk mengedit
              </span>
            ) : (
              <span className="ml-2 inline-flex items-center gap-1 text-[11px] text-slate-500 italic">
                <Info size={12} /> Anda melihat preview saja — untuk edit personel, minta admin aktifkan akses <b>Personnel Manage</b> di User Management → Access Control.
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canCreateUser && (
            <Button
              data-testid="btn-team-add-user"
              onClick={openAddUser}
              className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13] uppercase text-xs tracking-wider font-semibold h-9"
            >
              <Plus size={14} className="mr-1" /> Add User
            </Button>
          )}
          {canPrintPayroll && (
            <Button
              data-testid="btn-payroll-batch-print"
              onClick={() => { setPayrollPwd(""); setPayrollDlg(true); }}
              variant="outline"
              className="rounded-none border-[#0B1D3A] text-[#0B1D3A] hover:bg-[#0B1D3A] hover:text-white uppercase text-xs tracking-wider font-semibold h-9"
            >
              <Printer size={14} className="mr-1" /> Payroll Batch Print
            </Button>
          )}
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="team-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cari nama / email / divisi…"
              className="rounded-none h-9 pl-8 pr-3 text-xs border border-slate-300 w-64"
            />
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-slate-200" data-testid="team-tabs">
        <button
          type="button"
          data-testid="tab-team-list"
          onClick={() => setTab("list")}
          className={`px-4 py-2 text-xs uppercase tracking-wider font-semibold border-b-2 inline-flex items-center gap-1.5 ${tab === "list" ? "border-[#FF5A1F] text-[#FF5A1F]" : "border-transparent text-slate-500 hover:text-[#0B1D3A]"}`}
        ><Users size={13} /> Daftar Personil</button>
        <button
          type="button"
          data-testid="tab-team-org"
          onClick={() => setTab("org")}
          className={`px-4 py-2 text-xs uppercase tracking-wider font-semibold border-b-2 inline-flex items-center gap-1.5 ${tab === "org" ? "border-[#FF5A1F] text-[#FF5A1F]" : "border-transparent text-slate-500 hover:text-[#0B1D3A]"}`}
        ><Network size={13} /> Struktur Organisasi</button>
        <button
          type="button"
          data-testid="tab-team-by-city"
          onClick={() => setTab("by-city")}
          className={`px-4 py-2 text-xs uppercase tracking-wider font-semibold border-b-2 inline-flex items-center gap-1.5 ${tab === "by-city" ? "border-[#FF5A1F] text-[#FF5A1F]" : "border-transparent text-slate-500 hover:text-[#0B1D3A]"}`}
        ><MapPin size={13} /> Per Kota (Staff Base)</button>
      </div>

      {tab === "list" && (
        <>
          <OnLeaveWidget />
          <TeamListView users={users} query={query} cityFilter={cityFilter} onCityFilterChange={setCityFilter} canManage={canManage} onSelect={setSelectedId} viewerId={user?.id} canSeeAllLevels={canSeeAllLevels} />
        </>
      )}

      {tab === "org" && (
        <OrgChartView users={users} query={query} canManage={canManage} onSelect={setSelectedId} viewerId={user?.id} canSeeAllLevels={canSeeAllLevels} />
      )}

      {tab === "by-city" && (
        <TeamByCityView users={users} query={query} canManage={canManage} onSelect={setSelectedId} viewerId={user?.id} canSeeAllLevels={canSeeAllLevels} />
      )}

      {canManage && (
        <ProfileDialog
          key={selectedId || "closed"}
          open={!!selectedId}
          onOpenChange={(o) => { if (!o) onDialogClose(); }}
          targetUserId={selectedId}
        />
      )}

      {/* 149-cw-r149: Add User dialog — reuse component yg sama dgn User Management */}
      {canCreateUser && (
        <UserFormDialog
          dialog={addUserDialog}
          setDialog={setAddUserDialog}
          onSubmit={submitAddUser}
          busy={addUserBusy}
          divisions={divisions}
          subDivisions={subDivisions}
          positions={positions}
        />
      )}

      {/* Payroll Batch Print — requires password re-confirmation */}
      <Dialog open={payrollDlg} onOpenChange={(o) => { if (!payrollBusy) { setPayrollDlg(o); if (!o) setPayrollPwd(""); } }}>
        <DialogContent className="rounded-none max-w-md" data-testid="dialog-payroll-batch">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight inline-flex items-center gap-2">
              <Lock size={16} className="text-amber-600" /> Konfirmasi Payroll Batch Print
            </DialogTitle>
            <DialogDescription>
              Fitur sensitif — akan mencetak slip gaji <span className="font-semibold">semua karyawan</span> dalam satu dokumen PDF. Masukkan password login Anda untuk konfirmasi identitas.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (!payrollPwd) { toast.error("Masukkan password Anda"); return; }
              setPayrollBusy(true);
              try {
                const { data } = await api.post("/admin/payroll-slips", { password: payrollPwd });
                printPayrollBatch(data || []);
                setPayrollDlg(false);
                setPayrollPwd("");
              } catch (err) {
                const status = err.response?.status;
                if (status === 401) toast.error("Password salah");
                else toast.error(formatDetail(err.response?.data?.detail) || "Gagal load payroll");
              } finally { setPayrollBusy(false); }
            }}
            className="space-y-3"
          >
            <div className="space-y-1.5">
              <Label className="overline text-slate-600">Password Login</Label>
              <Input
                data-testid="payroll-pwd-input"
                type="password"
                value={payrollPwd}
                onChange={(e) => setPayrollPwd(e.target.value)}
                className="rounded-none"
                placeholder="Ketik password akun Anda"
                autoFocus
                disabled={payrollBusy}
              />
              <p className="text-[10px] text-slate-500">Password sama dengan yang Anda gunakan saat login. Server akan memverifikasi via bcrypt — tidak disimpan di browser.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-none" onClick={() => { setPayrollDlg(false); setPayrollPwd(""); }} disabled={payrollBusy}>
                Batal
              </Button>
              <Button type="submit" data-testid="payroll-pwd-submit" disabled={payrollBusy || !payrollPwd} className="rounded-none bg-[#0B1D3A] hover:bg-[#132a5b]">
                {payrollBusy ? "Memverifikasi…" : "Konfirmasi & Print"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TeamListView({ users, query, cityFilter, onCityFilterChange, canManage, onSelect, viewerId, canSeeAllLevels }) {
  const cityOptions = useMemo(() => {
    const set = new Set(users.map((u) => u.staff_base).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "id"));
  }, [users]);
  const filtered = users.filter((u) => {
    if (cityFilter && u.staff_base !== cityFilter) return false;
    if (!query) return true;
    const q = query.toLowerCase();
    return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q) || (u.division || "").toLowerCase().includes(q) || (u.sub_division || "").toLowerCase().includes(q) || (u.role_title || "").toLowerCase().includes(q) || (u.staff_base || "").toLowerCase().includes(q);
  });
  const grouped = filtered.reduce((acc, u) => {
    const key = u.division === "Project" ? `${u.division} · ${u.sub_division}` : u.division;
    (acc[key] = acc[key] || []).push(u);
    return acc;
  }, {});
  return (
    <>
      {cityOptions.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="team-city-filter">
          <span className="overline text-slate-500">Filter Kota Rekrut:</span>
          <button
            type="button"
            data-testid="team-city-filter-all"
            onClick={() => onCityFilterChange("")}
            className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold border ${cityFilter === "" ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "bg-white text-slate-600 border-slate-300 hover:border-[#0B1D3A]"}`}
          >Semua · {users.length}</button>
          {cityOptions.map((c) => {
            const count = users.filter((u) => u.staff_base === c).length;
            return (
              <button
                key={c}
                type="button"
                data-testid={`team-city-filter-${c}`}
                onClick={() => onCityFilterChange(c)}
                className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold border ${cityFilter === c ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "bg-white text-slate-600 border-slate-300 hover:border-[#0B1D3A]"}`}
              >{c} · <span className="tabular-nums opacity-80">{count}</span></button>
            );
          })}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {Object.entries(grouped).map(([label, list]) => (
          <section key={label} className="bg-white border border-slate-200" data-testid={`team-section-${label}`}>
            <header className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-display text-base tracking-tight">{label}</h2>
              <span className="text-xs tabular-nums text-slate-500">{list.length} personnel</span>
            </header>
            <ul>
              {list.map((u) => (
                <PersonRow key={u.id} u={u} canManage={canManage} onSelect={onSelect} showLevel={canSeeAllLevels || u.id === viewerId} />
              ))}
            </ul>
          </section>
        ))}
      </div>
    </>
  );
}

function TeamByCityView({ users, query, canManage, onSelect, viewerId, canSeeAllLevels }) {
  const filtered = users.filter((u) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (u.name || "").toLowerCase().includes(q) || (u.staff_base || "").toLowerCase().includes(q);
  });
  const groups = useMemo(() => {
    const map = new Map();
    for (const u of filtered) {
      const key = u.staff_base || "(Belum diisi)";
      const arr = map.get(key) || [];
      arr.push(u);
      map.set(key, arr);
    }
    return Array.from(map.entries()).sort((a, b) => {
      // "(Belum diisi)" always last
      if (a[0].startsWith("(") && !b[0].startsWith("(")) return 1;
      if (!a[0].startsWith("(") && b[0].startsWith("(")) return -1;
      // By count desc, then alpha
      if (b[1].length !== a[1].length) return b[1].length - a[1].length;
      return a[0].localeCompare(b[0], "id");
    });
  }, [filtered]);
  const totalWithCity = filtered.filter((u) => u.staff_base).length;
  return (
    <div className="space-y-4">
      <div className="bg-slate-50 border border-slate-200 p-3 text-[11px] text-slate-600 flex items-center gap-4 flex-wrap">
        <span><MapPin size={11} className="inline -mt-0.5 mr-1 text-[#FF5A1F]" /><span className="font-semibold text-slate-700">{groups.filter((g) => !g[0].startsWith("(")).length}</span> kota</span>
        <span><span className="font-semibold text-slate-700">{totalWithCity}</span> personil ter-map</span>
        {filtered.length - totalWithCity > 0 && (
          <span className="text-amber-700"><span className="font-semibold">{filtered.length - totalWithCity}</span> belum diisi</span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="team-by-city-groups">
        {groups.map(([city, list]) => (
          <section key={city} className="bg-white border border-slate-200" data-testid={`team-city-group-${city}`}>
            <header className={`px-4 py-3 border-b border-slate-200 flex items-center justify-between ${city.startsWith("(") ? "bg-amber-50" : "bg-slate-50"}`}>
              <div className="flex items-center gap-2 min-w-0">
                <MapPin size={13} className={city.startsWith("(") ? "text-amber-600 shrink-0" : "text-[#FF5A1F] shrink-0"} />
                <h2 className="font-display text-base tracking-tight truncate">{city}</h2>
              </div>
              <span className="text-xs tabular-nums text-slate-500 shrink-0">{list.length}</span>
            </header>
            <ul>
              {list.map((u) => (
                <PersonRow key={u.id} u={u} canManage={canManage} onSelect={onSelect} compact showLevel={canSeeAllLevels || u.id === viewerId} />
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}

function PersonRow({ u, canManage, onSelect, compact = false, showLevel = true }) {
  const clickable = canManage;
  const Wrapper = clickable ? "button" : "div";
  const wrapperProps = clickable
    ? {
        type: "button",
        onClick: () => onSelect(u.id),
        "data-testid": `team-personnel-${u.id}`,
        className: `w-full text-left ${compact ? "px-2 py-1.5" : "px-4 py-3"} border-b border-slate-100 flex items-center gap-3 hover:bg-slate-50 focus:bg-slate-50 focus:outline-none transition-colors group`,
      }
    : {
        "data-testid": `team-personnel-${u.id}`,
        className: `${compact ? "px-2 py-1.5" : "px-4 py-3"} border-b border-slate-100 flex items-center gap-3`,
      };
  return (
    <li>
      <Wrapper {...wrapperProps}>
        <div className={`${compact ? "w-7 h-7 text-[10px]" : "w-9 h-9 text-sm"} bg-[#0B1D3A] text-white flex items-center justify-center font-display font-semibold shrink-0`}>
          {u.name.split(" ").slice(0, 2).map((n) => n[0]).join("")}
        </div>
        <div className="min-w-0 flex-1">
          <div className={`font-medium ${compact ? "text-xs" : "text-sm"} text-[#0B1D3A] truncate group-hover:text-[#FF5A1F] transition-colors`}>{u.name}</div>
          <div className="text-[10px] text-slate-500 truncate">
            {u.role_title ? <span className="font-semibold text-slate-600">{u.role_title}</span> : u.email}
          </div>
          {u.position && (
            <div className="text-[10px] text-[#FF5A1F] truncate mt-0.5" data-testid={`team-position-${u.id}`}>
              <span className="uppercase tracking-wider font-semibold mr-1">Position:</span>{u.position}
            </div>
          )}
          {u.staff_base && (
            <div className="text-[10px] text-slate-600 truncate mt-0.5 inline-flex items-center gap-1" data-testid={`team-staff-base-${u.id}`}>
              <MapPin size={9} className="text-slate-400" />
              <span>{u.staff_base}</span>
            </div>
          )}
          {u.phone && (
            <div className="text-[10px] text-slate-600 truncate mt-0.5 inline-flex items-center gap-1" data-testid={`team-phone-${u.id}`}>
              <Phone size={9} className="text-slate-400" />
              <a
                href={`tel:${u.phone}`}
                onClick={(e) => e.stopPropagation()}
                className="hover:text-[#FF5A1F] hover:underline tabular-nums"
              >{u.phone}</a>
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className={`px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${DIVISION_COLOR[u.division]}`}>{u.division}</span>
          {showLevel && (
            <span className="px-1.5 py-0.5 text-[9px] bg-slate-100 font-semibold uppercase tracking-wider">Lv{u.level}</span>
          )}
        </div>
        {clickable && !compact && (
          <ChevronRight size={16} className="text-slate-300 group-hover:text-[#FF5A1F] shrink-0" />
        )}
      </Wrapper>
    </li>
  );
}

function OrgChartView({ users, query, canManage, onSelect, viewerId, canSeeAllLevels }) {
  // Build tree from reports_to. Root = users with no reports_to OR reports_to points to non-existent user.
  const tree = useMemo(() => buildTree(users), [users]);
  const [view, setView] = useState("visual"); // "visual" | "list"
  return (
    <div className="space-y-4">
      <div className="bg-slate-50 border border-slate-200 p-3 text-[11px] text-slate-600">
        <div className="flex flex-wrap items-center gap-2 justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <Network size={14} className="text-[#FF5A1F]" />
            <span className="font-semibold">Struktur Organisasi</span>
            <span className="text-slate-500">— dibentuk dari field <code className="text-[10px] bg-white border border-slate-200 px-1">reports_to</code> pada profil tiap personil.</span>
          </div>
          <div className="inline-flex border border-slate-300 bg-white">
            <button
              type="button"
              data-testid="org-view-visual"
              onClick={() => setView("visual")}
              className={`px-2.5 py-1 text-[10px] uppercase tracking-wider font-semibold ${view === "visual" ? "bg-[#0B1D3A] text-white" : "text-slate-600 hover:bg-slate-50"}`}
            >Bagan Visual</button>
            <button
              type="button"
              data-testid="org-view-list"
              onClick={() => setView("list")}
              className={`px-2.5 py-1 text-[10px] uppercase tracking-wider font-semibold border-l border-slate-300 ${view === "list" ? "bg-[#0B1D3A] text-white" : "text-slate-600 hover:bg-slate-50"}`}
            >Daftar Tree</button>
          </div>
        </div>
        {canManage && (
          <div className="mt-1 text-slate-500">
            Klik personil untuk mengubah <span className="font-semibold">Jabatan (Role Title)</span> dan <span className="font-semibold">Melapor Kepada</span> — lihat section &quot;Struktur Organisasi&quot; di ProfileDialog.
          </div>
        )}
      </div>
      {tree.length === 0 ? (
        <div className="text-xs text-slate-500 italic py-8 text-center">Belum ada data personil.</div>
      ) : view === "visual" ? (
        <OrgChartVisual roots={tree} query={query} canManage={canManage} canEditTiers={canManage} onSelect={onSelect} viewerId={viewerId} canSeeAllLevels={canSeeAllLevels} />
      ) : (
        <OrgTree roots={tree} query={query} canManage={canManage} onSelect={onSelect} viewerId={viewerId} canSeeAllLevels={canSeeAllLevels} />
      )}
    </div>
  );
}

function buildTree(users) {
  const byId = new Map(users.map((u) => [u.id, { ...u, children: [] }]));
  const roots = [];
  users.forEach((u) => {
    const node = byId.get(u.id);
    const parentId = u.reports_to;
    if (parentId && byId.has(parentId)) {
      byId.get(parentId).children.push(node);
    } else {
      roots.push(node);
    }
  });
  // Sort each level by division order then level then name
  const sortFn = (a, b) => {
    const da = DIV_ORDER.indexOf(a.division); const db = DIV_ORDER.indexOf(b.division);
    if (da !== db) return da - db;
    if (a.level !== b.level) return a.level - b.level;
    return (a.name || "").localeCompare(b.name || "");
  };
  const walk = (node) => {
    node.children.sort(sortFn);
    node.children.forEach(walk);
  };
  roots.sort(sortFn);
  roots.forEach(walk);
  return roots;
}

function OrgNode_INLINE_STUB() { return null; /* replaced by <OrgTree /> */ }

/**
 * Payroll Batch Print — buka window baru dengan HTML multi-halaman berisi
 * SEMUA slip gaji karyawan (1 slip = 1 halaman A4). User lalu Save as PDF.
 */
function printPayrollBatch(slips) {
  if (!slips || slips.length === 0) {
    alert("Tidak ada data payroll untuk dicetak.");
    return;
  }
  const idr = (v) => "Rp " + (Number(v) || 0).toLocaleString("id-ID");
  const today = new Date();
  const period = today.toLocaleDateString("id-ID", { month: "long", year: "numeric" });
  const printedAt = today.toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" }) + " " + today.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" });
  const slipHtml = (s, idx, last) => {
    const b = s.pph_breakdown || {};
    return `<section class="slip">
      <div class="company">
        <div class="brand">
          <div class="logo">ENS</div>
          <div>
            <div class="name">PT. EKA NUSA SINERGI</div>
            <div class="tag">Contractor · Construction · Development</div>
          </div>
        </div>
        <div class="doc-info">
          <strong>SLIP GAJI</strong>
          <div>Periode: ${period}</div>
          <div>Halaman: ${idx + 1} / ${slips.length}</div>
        </div>
      </div>
      <div class="meta">
        <div class="cell"><div class="k">Nama Karyawan</div><div class="v">${s.employee_name || "-"}</div></div>
        <div class="cell"><div class="k">Divisi / Sub-Divisi</div><div class="v">${s.employee_division || "-"} · ${s.employee_sub_division || "-"}</div></div>
        <div class="cell"><div class="k">Jabatan / Posisi</div><div class="v">${s.employee_position || "-"}</div></div>
        <div class="cell"><div class="k">Status PTKP</div><div class="v">${s.employee_ptkp_status || b.ptkp_status || "-"}</div></div>
      </div>
      <table>
        <thead><tr><th class="th-pay">Pendapatan (Earnings)</th><th class="th-pay num" style="width: 30%">Jumlah (IDR)</th></tr></thead>
        <tbody>
          <tr><td>Gaji Pokok</td><td class="num">${idr(s.base_salary)}</td></tr>
          <tr><td>Tunjangan Makan</td><td class="num">${idr(s.allowance_meal)}</td></tr>
          <tr><td>Tunjangan Transport</td><td class="num">${idr(s.allowance_transport)}</td></tr>
          <tr><td>Tunjangan Lain-Lain</td><td class="num">${idr(s.allowance_other)}</td></tr>
          <tr class="total"><td>Total Pendapatan (Gross)</td><td class="num">${idr(s.gross)}</td></tr>
        </tbody>
      </table>
      <table>
        <thead><tr><th class="th-ded">Potongan (Deductions)</th><th class="th-ded num" style="width: 30%">Jumlah (IDR)</th></tr></thead>
        <tbody>
          <tr><td>BPJS Ketenagakerjaan — JHT (2%)</td><td class="num">${idr(s.bpjs_tk?.jht_employee)}</td></tr>
          <tr><td>BPJS Ketenagakerjaan — JP (1%, cap 9,5jt)</td><td class="num">${idr(s.bpjs_tk?.jp_employee)}</td></tr>
          <tr><td>PPh 21 (${s.pph_auto_computed ? "otomatis, PTKP " + (b.ptkp_status || "-") : "manual override"})</td><td class="num">${idr(s.pph_amount)}</td></tr>
          <tr class="total"><td>Total Potongan</td><td class="num">${idr((s.bpjs_tk?.total || 0) + (s.pph_amount || 0))}</td></tr>
        </tbody>
      </table>
      <div class="thp">
        <div class="k">Take Home Pay</div>
        <div class="v">${idr(s.take_home_pay)}</div>
      </div>
      <div class="foot">
        <div class="sig">
          <div class="line"></div>
          <div class="role">Karyawan</div>
          <div style="margin-top:2px; font-weight:600;">${s.employee_name || ""}</div>
        </div>
        <div class="sig">
          <div class="line"></div>
          <div class="role">HR / Finance</div>
          <div style="margin-top:2px; font-weight:600;">PT. Eka Nusa Sinergi</div>
        </div>
      </div>
      <div class="notice"><strong>Catatan:</strong> Dokumen internal PT. Eka Nusa Sinergi. Perhitungan PPh 21 mengikuti UU No. 7/2021 (HPP) &amp; PMK-168/2023. BPJS Ketenagakerjaan dihitung dari komponen gaji pokok. Dicetak batch pada ${printedAt}.</div>
    </section>${last ? "" : '<div class="page-break"></div>'}`;
  };
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Payroll Batch · ${period}</title>
<style>
  @page { size: A4; margin: 18mm 14mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #0B1D3A; margin: 0; padding: 0; }
  .slip { page-break-inside: avoid; }
  .page-break { page-break-after: always; height: 0; }
  .company { border-bottom: 3px solid #0B1D3A; padding-bottom: 8px; margin-bottom: 16px; display: flex; align-items: center; justify-content: space-between; }
  .company .brand { display: flex; align-items: center; gap: 12px; }
  .company .logo { width: 44px; height: 44px; background: #0B1D3A; color: #FF5A1F; font-weight: 900; font-size: 20px; display: flex; align-items: center; justify-content: center; letter-spacing: -1px; }
  .company .name { font-size: 16px; font-weight: 700; letter-spacing: -0.5px; }
  .company .tag { font-size: 9px; color: #64748b; text-transform: uppercase; letter-spacing: 1.5px; }
  .company .doc-info { text-align: right; font-size: 9px; color: #64748b; text-transform: uppercase; letter-spacing: 1px; }
  .doc-info strong { display: block; color: #0B1D3A; font-size: 14px; letter-spacing: -0.3px; margin-bottom: 2px; text-transform: none; }
  .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 16px; margin-bottom: 16px; padding: 10px 12px; background: #f8fafc; border: 1px solid #e2e8f0; }
  .meta .cell { font-size: 10px; }
  .meta .cell .k { font-size: 8.5px; color: #64748b; text-transform: uppercase; letter-spacing: 1px; }
  .meta .cell .v { font-size: 11px; color: #0B1D3A; font-weight: 600; margin-top: 1px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 14px; }
  table th { text-align: left; background: #0B1D3A; color: #fff; padding: 6px 10px; font-size: 9.5px; text-transform: uppercase; letter-spacing: 1px; font-weight: 600; }
  table td { padding: 5px 10px; border-bottom: 1px solid #e2e8f0; font-size: 11px; }
  table td.num { text-align: right; font-variant-numeric: tabular-nums; }
  table tr.total td { font-weight: 700; border-top: 2px solid #0B1D3A; border-bottom: none; background: #f1f5f9; }
  .th-pay { background: #10b981 !important; }
  .th-ded { background: #f43f5e !important; }
  .thp { margin-top: 6px; background: #0B1D3A; color: #fff; padding: 12px 14px; display: flex; justify-content: space-between; align-items: center; }
  .thp .k { font-size: 10px; text-transform: uppercase; letter-spacing: 2px; opacity: 0.85; }
  .thp .v { font-size: 22px; font-weight: 800; color: #FF5A1F; font-variant-numeric: tabular-nums; letter-spacing: -0.5px; }
  .foot { display: grid; grid-template-columns: 1fr 1fr; gap: 60px; margin-top: 40px; font-size: 10px; }
  .foot .sig { text-align: center; }
  .foot .sig .line { border-top: 1px solid #0B1D3A; margin: 60px 0 4px; }
  .foot .sig .role { color: #64748b; font-size: 9px; text-transform: uppercase; letter-spacing: 1px; }
  .notice { margin-top: 24px; padding: 8px 10px; background: #fef3c7; border-left: 3px solid #f59e0b; font-size: 9px; color: #78350f; line-height: 1.5; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style></head><body>
  ${slips.map((s, i) => slipHtml(s, i, i === slips.length - 1)).join("")}
  <script>window.onload = function() { setTimeout(function() { window.print(); }, 400); };</script>
</body></html>`;
  // XSS-safe: pakai Blob + object URL (bukan document.write) supaya CSP-friendly.
  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank", "width=900,height=1000");
  if (!w) { URL.revokeObjectURL(url); alert("Popup diblokir browser. Izinkan popup untuk print payroll batch."); return; }
  // Revoke URL setelah print window sempat load & trigger window.print()
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

